import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';

// ── API calls (isolated — only used by this feature) ──────────────────────────

const aiSummaryApi = {
  getStatus:    ()           => api.get('/ai/summary/status'),
  getSummary:   (id)         => api.get(`/ai/summary/${id}`),
  regenerate:   (id)         => api.post(`/ai/summary/${id}/regenerate`),
};

// ── Hooks ──────────────────────────────────────────────────────────────────────

/**
 * Checks whether the AI summary feature is enabled on the server.
 * Result is cached for the session — the flag only changes with a deployment.
 */
export function useAiStatus() {
  return useQuery(
    ['ai-summary-status'],
    () => aiSummaryApi.getStatus(),
    {
      staleTime: Infinity,          // feature flag doesn't change at runtime
      retry: false,                 // if it fails, treat as disabled
      onError: () => {},            // swallow errors silently — feature is optional
    }
  );
}

/**
 * Fetches (or auto-generates) the AI summary for a given assignment.
 * Only runs when the feature is enabled and an assignmentId is provided.
 *
 * @param {number|string} assignmentId
 * @param {boolean}       featureEnabled  — pass the value from useAiStatus()
 */
export function useAiSummary(assignmentId, featureEnabled) {
  return useQuery(
    ['ai-summary', assignmentId],
    () => aiSummaryApi.getSummary(assignmentId),
    {
      enabled: !!assignmentId && !!featureEnabled,
      staleTime: 5 * 60 * 1000,    // treat a cached summary as fresh for 5 min
      retry: 1,
      onError: () => {},
    }
  );
}

/**
 * Mutation to force-regenerate the summary (discards cache, calls Claude again).
 *
 * @param {number|string} assignmentId
 */
export function useRegenerateSummary(assignmentId) {
  const qc = useQueryClient();
  return useMutation(
    () => aiSummaryApi.regenerate(assignmentId),
    {
      onSuccess: (data) => {
        qc.setQueryData(['ai-summary', assignmentId], data);
      },
    }
  );
}
